import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import i18next from 'i18next';
import getConfig from 'next/config';
import classNames from 'classnames';
import throttle from 'lodash.throttle';
import { stub as $t } from '@nbcnews/analytics-framework';
import { useInView } from 'react-intersection-observer';

import { Button } from 'components/Button';
import { shouldTargetBeBlankHelper } from 'lib/shouldTargetBeBlankHelper';
import { withViewportVisibility } from 'lib/Hooks/withViewportVisibility';
import { addProductToCart } from 'lib/northfork';
import { getDisplayNameForVertical } from 'lib/vertical';
import { getDataActivityMapID } from 'lib/articleUtils';
import { getAmazonProductOffersTrackingObject } from 'lib/commerceUtils';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { useSliderPagination } from 'lib/Hooks/useSliderPagination';
import {
  SEE_MORE_BUTTON,
} from 'lib/brandFeatures';
import { FeatureFlagContext, LayoutContext } from 'lib/ContextTypes';

import { DisclaimerModal } from 'components/Disclaimer';
import Link from 'components/Link';
import TitleLogo from 'components/TitleLogo';
import PackageTitle from 'components/PackageTitle';
import { getProductOfferIdsFromProducts, getProductOfferPrices } from 'lib/productOfferPriceHelpers';
import { NotchedHorizontalRule } from 'components/NotchedHorizontalRule';
import { isProductBacon, isShopListByOneProduct } from './Cards/helper';

import { CardSelector } from './Cards/CardSelector';

import styles from './styles.module.scss';

$t('register', 'mbt_bacon_click', { allowDuplicate: true });
$t('register', 'rmn_amazon_products_known', { allowDuplicate: true });

export const BACON_CARD_WIDTHS = {
  default: 260,
  author: 263,
  oneOneSmall: 260,
  oneOneProduct: 260,
  shopListByOneProduct: 260,
  twoOneSmall: 260,
  /**
   * This is a special case for the twoOneLargeItems card type.
   * @param {boolean} isMobile - Flag to determine if the device is mobile.
   */
  twoOneLarge: (isMobile) => (isMobile ? 260 : 360),
  /**
   * This is a special case for the twoOneLargeItems card type.
   * @param {boolean} isMobile - Flag to determine if the device is mobile.
   */
  twoOneLargeItems: (isMobile) => (isMobile ? 228 : 300),
  twoThreePortrait: 250,
  /**
   * This is a special case for the twoOneLargeItems card type.
   * @param {boolean} isMobile - Flag to determine if the device is mobile.
   */
  twoOneLargeVideoPage: (isMobile) => (isMobile ? 260 : 220),
};

const ADD_ALL_ITEMS_TO_CART_TEXT = 'Add All Items To Cart';

const BACON_BUTTON_WIDTH = 50;

/**
 * Get the data activity map ID for the bacon component.
 * @param {object} root0 - The root object.
 * @param {Array<object>} root0.items - The items in the bacon component.
 * @param {string} root0.trackingId - The tracking ID.
 */
export const appendTrackingIdParams = ({ items, trackingId }) => {
  let modifiedItems = items;

  if (trackingId) {
    const {
      publicRuntimeConfig: {
        CODE_ENVIRONMENT,
      },
    } = getConfig();

    const modifier = CODE_ENVIRONMENT === 'production' ? '' : 'test';

    if (modifier.length > 0) {
      modifiedItems = modifiedItems.map((i) => {
        const offers = i?.item?.offers || [];
        const modifiedOffers = offers.map((o) => (
          { ...o, externalUrl: o.externalUrl.replace(trackingId, `${trackingId}${modifier}`) }
        ));
        return { ...i, item: { ...i.item, offers: modifiedOffers } };
      });
    }
  }
  return modifiedItems;
};

/**
 * Bacon component.
 * @param {object} props - The component props.
 * @param {object} props.content - The content object.
 * @param {string} props.pageRegion - The page region.
 * @param {string} props.vertical - The vertical.
 * @param {Function} props.trackLink - The function to track link clicks.
 * @param {Function} props.onScroll - The function to handle scroll events.
 * @param {boolean} props.shouldShowAdditionalSeller - Flag to show additional seller.
 * @param {boolean} props.inlineArticle - Flag to show inline article.
 * @param {string} props.titleBgColor - The background color for the title.
 * @param {string} props.titleSize - The size of the title.
 * @param {boolean} props.useAltTitle - Flag to use alternate title.
 * @param {boolean} props.shouldOverrideShortDisclaimer - Flag to override short disclaimer.
 * @param {boolean} props.showStripeBackground - Flag to show stripe background.
 * @param {string} [props.presentationStyle] - The optional presentation style.
 */
const Bacon = ({
  content,
  pageRegion = 'article-body',
  vertical,
  trackLink = () => { },
  onScroll = () => { },
  shouldShowAdditionalSeller = false,
  inlineArticle = false,
  titleBgColor = null,
  titleSize = null,
  useAltTitle = false,
  shouldOverrideShortDisclaimer = false,
  showStripeBackground = true,
  presentationStyle = '',
}) => {
  const scrollContainerRef = useRef();
  const [addAllToCartText, setAddAllToCartText] = useState(ADD_ALL_ITEMS_TO_CART_TEXT);
  const { isFluidWidthPage } = React.useContext(LayoutContext);
  const [disclaimer, setDisclaimer] = useState(null);

  const {
    currentPage,
    totalPages,
    shouldShowPagination: showPagination,
    handleScrollToPage,
    handleScrollPrev,
    handleScrollNext,
    onContainerScroll,
  } = useSliderPagination(
    {
      cardWrapperClass: styles.baconCardWrapper,
      fallbackCardWidth: BACON_CARD_WIDTHS[content?.cardType] || BACON_CARD_WIDTHS.default,
      onScroll,
      paginationButtonSize: BACON_BUTTON_WIDTH,
      scrollContainerRef,
    },
  );

  const [ref, inView] = useInView({
    initialInView: false,
    triggerOnce: true,
    rootMargin: '250px 0px',
  });

  useEffect(() => {
    if (!inView) return;

    const products = content?.items?.map((ci) => ci?.item)?.filter((item) => item?.type === 'product');

    const amazonProductOffersTrackingObject = getAmazonProductOffersTrackingObject(products);

    if (amazonProductOffersTrackingObject) {
      $t('track', 'rmn_amazon_products_known', amazonProductOffersTrackingObject);
    }
  }, [inView]);

  /**
   * Set the click tracking for the bacon component.
   * @param {object} object - The object containing the tracking data.
   */
  const setMbtBaconClickTracking = (object) => {
    $t('track', 'mbt_bacon_click', { ...object, content, ...(presentationStyle && { presentation: presentationStyle }) });
  };


  /**
   * Render the title of the bacon component.
   */
  const renderTitle = () => {
    const { metadata, subType } = content;
    const {
      logoUrl,
      seeAllText,
      seeAllUrl,
      title,
      icid,
      anchorId,
    } = metadata || {};

    const shouldRenderButton = getFeatureConfigForBrand(
      SEE_MORE_BUTTON,
      vertical,
    );
    const showSeeAll = !isShopListByOneProduct(subType) && !shouldRenderButton && !!seeAllUrl;
    const titleClass = classNames(styles.baconTitle, {
      [styles.shopList]: isShopListByOneProduct(subType),
    });

    if (!(title || logoUrl)) return null;

    const pkgTitleClassNames = classNames('bacon-pkg-title', {
      'bacon-pkg-title--inline-article': inlineArticle,
    });

    return (
      <div className={styles.baconHeaderHeadline}>
        <button
          onClick={() => setMbtBaconClickTracking({
            action: 'navClick',
            type: 'bacon-header',
          })}
          type="button"
          className={styles.baconHeaderHeadlineBtn}
          data-test="bacon__header__headline"
          data-testid="bacon__header"
        >

          {(title || logoUrl) && (
            (useAltTitle || isFluidWidthPage) ? (
              <PackageTitle
                metadata={{
                  title, seeAllUrl, icid, logoUrl, anchorId,
                }}
                titleBgColor={titleBgColor}
                size={titleSize}
                className={pkgTitleClassNames}
              />
            ) : (
              <h2
                className={titleClass}
                data-testid="bacon__headline"
              >
                <>
                  <TitleLogo logoUrl={logoUrl} title={title} />
                  {showSeeAll ? (
                    <Link
                      className={styles.baconTitleLink}
                      data-testid="bacon__header__see-all-link"
                      to={seeAllUrl}
                      icid={icid}
                    >
                      {seeAllText || i18next.t('SEE ALL')}
                    </Link>
                  ) : null}
                </>
              </h2>
            )
          )}
        </button>
      </div>
    );
  };

  /**
   * Check if the disclaimer should be shown.
   */
  const hasDisclaimer = () => {
    const { metadata, subType } = content;
    const {
      componentTitle,
      disclaimerKey,
      disclaimerOverride = false,
      ecommerceEnabled = true,
      shortDisclaimer = null,
    } = metadata || {};

    if (disclaimerKey === 'NONE') return null;

    if (componentTitle === 'curated-list' || shouldOverrideShortDisclaimer) {
      if (shortDisclaimer?.length > 0 && disclaimer !== shortDisclaimer) {
        setDisclaimer(shortDisclaimer);
      } else if (!shortDisclaimer && disclaimer !== null) {
        setDisclaimer(null);
      }
    }

    if (componentTitle === 'curated-list') {
      return disclaimerOverride && ecommerceEnabled && subType && subType.toLowerCase().includes('product');
    }

    return ecommerceEnabled && subType && subType.toLowerCase().includes('product');
  };


  /**
   * Render description.
   */
  const renderDescription = () => {
    const { subType, metadata } = content;
    const {
      componentTitle,
      description,
      seeAllText,
      seeAllUrl,
    } = metadata || {};

    if (!description) {
      return null;
    }

    const isCuratedList = componentTitle === 'curated-list';
    const isCurationProductBacon = isProductBacon(subType) && !isCuratedList;

    if (isShopListByOneProduct(subType) || isCurationProductBacon) {
      return (
        <div className={styles.description} data-testid="bacon__description--read-more">
          {description}
          {seeAllUrl && (
            <Link className={styles.readMoreLink} to={seeAllUrl}>
              {seeAllText || i18next.t('READ MORE')}
            </Link>
          )}
        </div>
      );
    }

    if (inlineArticle) {
      return (
        <div
          className={styles.description}
          data-testid="bacon__description--rich-text"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: description }}
        />
      );
    }
    return <div className={styles.description} data-testid="bacon__description">{description}</div>;
  };

  /**
   * Render the button to add all items to cart.
   */
  const renderAllItemsToCartButton = () => {
    const { metadata, items } = content;
    const { trackingId } = metadata || {};

    const productIds = getProductOfferIdsFromProducts(items);

    const itemsForTracking = items
      .filter((i) => Boolean(i) && Boolean(i.item))
      .map((i) => {
        const { item } = i;
        const { name, offers } = item || {};
        const [offer = {}] = offers ?? [];
        const priceMap = getProductOfferPrices(offer);
        const seller = offer?.seller?.name ?? null;
        const listPrice = priceMap?.list ?? null;
        const salePrice = priceMap?.sale ?? null;
        return {
          name,
          seller,
          listPrice,
          salePrice,
        };
      });
    return (
      <button
        type="button"
        className={styles.allItemsToCart}
        onClick={() => {
          $t('track', 'mbt_bacon_click', {
            action: 'addAllItemsToCart',
            items: itemsForTracking,
          });
          addProductToCart(productIds, { trackingId });
          setAddAllToCartText('Added All Items To Cart');
          setTimeout(() => {
            setAddAllToCartText(ADD_ALL_ITEMS_TO_CART_TEXT);
          }, 600);
        }}
      >
        {addAllToCartText}
      </button>
    );
  };

  /**
   * Render the disclaimer.
   */
  const renderDisclaimer = () => {
    if (!hasDisclaimer()) return null;
    const { metadata } = content;
    const {
      disclaimerKey, disclaimerOverride, hideDisclaimerLearnMore, learnMoreLink,
    } = metadata || {};
    return (
      <DisclaimerModal
        disclaimerKey={disclaimerKey}
        disclaimerOverride={{ disclaimerOverride }}
        learnMoreLinkOverride={learnMoreLink}
        showCloseButton
        hideLearnMore={hideDisclaimerLearnMore}
        small
      >
        <>
          {disclaimer ?? `${getDisplayNameForVertical(vertical)} earns a commission on purchases through these links.`}
        </>
      </DisclaimerModal>
    );
  };

  /**
   * Render the paging dots.
   */
  const renderPagingDots = () => {
    if (!showPagination) {
      return null;
    }

    return (
      <div
        className={styles.baconPagingDots}
        data-testid="bacon__paging-dots"
      >
        {Array.from({ length: totalPages }).map((_, i) => {
          const dotPageNum = i + 1;
          const isCurrentPage = currentPage === dotPageNum;

          return (
            <button
              type="button"
              aria-label="Pagination"
              key={`${dotPageNum}/${totalPages}`}
              onClick={() => {
                handleScrollToPage(dotPageNum);
                setMbtBaconClickTracking({
                  action: 'navClick',
                  type: `pagingDotNumber: ${dotPageNum}`,
                });
              }}
              data-testid="bacon__header__paging-dot"
              className={classNames(
                styles.baconPagingDot,
                {
                  [styles.baconPagingDotIsCurrent]: isCurrentPage,
                },
              )}
            />
          );
        })}
      </div>
    );
  };

  /**
   * Render the previous button.
   */
  const renderPrevButton = () => {
    const { scrollLeft = 0 } = scrollContainerRef.current || {};

    if (currentPage === 1) return null;

    return (
      <div
        className={classNames(
          styles.baconPrevBtnContainer,
          { [styles.baconPrevBtnActive]: showPagination && scrollLeft !== 0 },
        )}
        data-testid="bacon__page-btns__prev-container"
      >
        <button
          type="button"
          className={styles.baconPageBtnPrev}
          onClick={() => {
            handleScrollPrev();
            setMbtBaconClickTracking({
              action: 'navClick',
              type: 'left arrow',
            });
          }}
          data-testid="bacon__page-btns__prev"
        >
          <span className={classNames(styles.baconPageBtnIcon, 'icon icon-angle-down')} />
        </button>
      </div>
    );
  };
  /**
   * Render the next button.
   */
  const renderNextButton = () => (
    <div
      className={classNames(
        styles.baconNextBtnContainer,
        { [styles.baconNextBtnContainerNoPagination]: !showPagination },
      )}
      data-testid="bacon__page-btns__next-container"
    >
      <button
        type="button"
        className={styles.baconPageBtnNext}
        onClick={throttle(() => {
          handleScrollNext();
          setMbtBaconClickTracking({
            action: 'navClick',
            type: 'right arrow',
          });
        }, 250, { leading: true, trailing: true })}
        data-testid="bacon__page-btns__next"
      >
        <span className={classNames(styles.baconPageBtnIcon, 'icon icon-angle-down')} />
      </button>
    </div>
  );

  /**
 * Render the see more button.
 */
  const renderSeeMoreButton = () => {
    const { metadata, subType } = content;
    const {
      seeAllText,
      seeAllUrl,
    } = metadata || {};

    const showSeeAll = !isShopListByOneProduct(subType) && seeAllUrl;

    return (
      showSeeAll && (
        <div>
          <Button
            size="normal"
            alignment="right"
            title={seeAllText || i18next.t('See More')}
            type="link"
            url={seeAllUrl}
          />
        </div>
      )
    );
  };

  /**
     * Render the footer of the bacon component.
     */
  const renderFooter = () => {
    const { metadata, subType } = content;
    const { sponsorLogoText, sponsorLogoUrl } = metadata || {};
    if (isShopListByOneProduct(subType) && sponsorLogoText && sponsorLogoUrl) {
      return (
        <div className={styles.footer} data-testid="bacon__footer--sponsor">
          <div>{sponsorLogoText}</div>
          <img className={styles.logo} alt="sponsor-logo" src={sponsorLogoUrl} />
        </div>
      );
    }
    return null;
  };
  const { metadata, subType, items: unfilteredItems = [] } = content;
  const {
    description,
    title,
    componentTitle,
    trackingId,
    showBottomBorder = false,
  } = metadata || {};

  const isCuratedList = componentTitle === 'curated-list';
  const isCurationProductBacon = isProductBacon(subType) && !isCuratedList;
  const items = unfilteredItems.filter(
    (i) => Boolean(i) && (i.type !== 'product' || (Boolean(i.id) && Boolean(i.item))),
  );

  if (items.length === 0) {
    return null;
  }

  const modifiedItems = appendTrackingIdParams({
    items,
    trackingId,
  });

  const hasDisclaimerResult = hasDisclaimer();
  const hasDescription = hasDisclaimerResult || !!description;
  const showItemDescriptions = Boolean(
    modifiedItems.find(
      (i) => i.item?.description?.primary || i.computedValues?.dek,
    ),
  );
  const dataActivityMapID = getDataActivityMapID(
    {
      componentName: 'bacon',
      pageRegion,
      componentTitle: componentTitle || title,
    },
  );

  const slideWidthClass = subType ?? content.cardType;
  const isTwoOneLargeItem = subType === 'twoOneLargeItems';
  const shouldRenderButton = getFeatureConfigForBrand(
    SEE_MORE_BUTTON,
    vertical,
  );
  return (
    <section
      className={classNames({
        [styles.stripe]: subType?.includes('Product') && showStripeBackground,
        isShowingPagination: showPagination,
        [styles.inlineArticle]: inlineArticle,
        [styles.twoOneLargeItemsBacon]: isTwoOneLargeItem,
      }, styles.baconPackage, 'pkg bacon')}
      data-packageid={content.id}
      data-activity-map={dataActivityMapID}
      data-testid="baconPackage"
      ref={ref}
    >
      <NotchedHorizontalRule className={styles.baconHeaderTopBorder} data-testid="bacon__header__top-border" />
      <div
        className={classNames(styles.baconHeader, {
          [styles.baconHeaderFull]: hasDescription,
          [styles.centerAlign]: isShopListByOneProduct(subType),
          [styles.flexColumn]: !hasDescription,
        })}
      >
        {renderTitle()}
        <div className={styles.baconHeaderInner}>
          {
            isCurationProductBacon && (
              <div
                className={styles.descriptionWrapper}
                data-testid="bacon__header__dek"
              >
                {renderDescription()}
                {renderDisclaimer()}
                <div className={styles.baconAddButtonPagingDots}>
                  {renderPagingDots()}
                </div>
              </div>
            )
          }
          {
            isShopListByOneProduct(subType) && (
              <div
                className={styles.descriptionWrapper}
                data-testid="bacon__header__dek"
              >
                {renderDescription()}
                {renderDisclaimer()}
                <div className={styles.baconAddButtonPagingDots}>
                  {renderAllItemsToCartButton()}
                  {renderPagingDots()}
                </div>
              </div>
            )
          }
          {
            inlineArticle && (
              <div
                className={styles.descriptionWrapper}
                data-testid="bacon__header__dek"
              >
                {renderDescription()}
                <div className={styles.baconDisclaimerPagingDots}>
                  {renderDisclaimer()}
                  {renderPagingDots()}
                </div>
              </div>
            )
          }
          {
            !inlineArticle
            && !isShopListByOneProduct(subType)
            && !isCurationProductBacon
            && hasDisclaimerResult
            && (
              <div
                className={styles.descriptionWrapper}
                data-testid="bacon__header__dek"
              >
                <div className={styles.baconDisclaimerPagingDots}>
                  {renderDisclaimer()}
                  {renderPagingDots()}
                </div>
              </div>
            )
          }
          {
            !inlineArticle
            && !isShopListByOneProduct(subType)
            && !isCurationProductBacon
            && !hasDisclaimerResult
            && (
              <>
                {renderDescription()}
                {renderPagingDots()}
              </>
            )
          }
        </div>
      </div>
      <div
        className={classNames(styles.baconScrollWrapper, {
          [styles.centerAlign]: isShopListByOneProduct(subType),
        })}
      >
        {renderPrevButton()}
        <FeatureFlagContext.Consumer>
          {(featureFlags) => (
            <div
              data-testid="bacon-scroll-container"
              className={styles.baconScrollContainer}
              ref={scrollContainerRef}
              onScroll={onContainerScroll}
            >
              {modifiedItems.map((itemObj, index) => {
                if (!itemObj || !itemObj?.computedValues) return null;
                const indexOffset = index + 1;
                // the id can either be a direct child or will be inside a nested object
                const keyVal = itemObj?.item?.id ?? itemObj.id;
                const item = itemObj?.item ?? itemObj;

                const { 'show-nbc-sports-front': sportsFeatureFlag } = featureFlags;
                const sourceName = item?.source?.name || item?.source?.organization?.name;
                const shouldOpenLinkInNewTab = sportsFeatureFlag ? shouldTargetBeBlankHelper(sourceName, item?.subType) : false;
                return (
                  <CardSelector
                    key={keyVal}
                    item={itemObj}
                    shouldShowAdditionalSeller={shouldShowAdditionalSeller}
                    totalNumberOfCards={modifiedItems.length}
                    indexOffset={indexOffset}
                    showItemDescriptions={showItemDescriptions}
                    content={content}
                    trackLink={() => trackLink(item)}
                    onAddProduct={(productId) => addProductToCart([productId], { trackingId })}
                    containerClasses={classNames(styles.baconCardWrapper, styles[slideWidthClass], {
                      lastItem: index === modifiedItems.length - 1,
                    })}
                    setMbtBaconClickTracking={setMbtBaconClickTracking}
                    vertical={vertical}
                    shouldOpenLinkInNewTab={shouldOpenLinkInNewTab}
                  />
                );
              })}
            </div>
          )}
        </FeatureFlagContext.Consumer>
        {renderNextButton()}
      </div>
      {renderFooter()}
      {showBottomBorder && (<hr className={styles.baconBottomBorder} data-testid="bacon__bottom-border" />)}
      {shouldRenderButton && renderSeeMoreButton()}
    </section>
  );
};

const LazyBacon = withViewportVisibility(Bacon, '300px');
export { Bacon, LazyBacon };
